import React, { useRef, useState, useEffect } from "react"
import PropTypes from "prop-types"
import { graphql } from "gatsby"

import Reveal from "../utils/Reveal"

import Section from "../atoms/Section"
import Flex from "../atoms/Flex"
import Box from "../atoms/Box"
import Button from "../atoms/Button"
import Icon from "../atoms/Icon"
import SectionTitle from "../molecules/SectionTitle"

export const renderIcon = ({ iconAttribute, iconStyles }) => {
  const img = iconAttribute?.image
  if (!img) return null

  const iconSize = iconStyles?.iconSize

  return (
    iconAttribute && (
      <Icon
        flexShrink={0}
        {...img}
        mb={4}
        width={
          iconStyles?.iconFit === "height"
            ? "auto"
            : iconStyles?.iconFit === "width" || iconSize
              ? `${iconSize}px`
              : 2
        }
        height={
          iconStyles?.iconFit === "width"
            ? "auto"
            : iconStyles?.iconFit === "height" || iconSize
              ? `${iconSize}px`
              : 2
        }
        maxWidth={
          iconStyles?.iconFit === "width" || iconStyles?.iconFit === "square"
            ? `${iconSize}px`
            : "auto"
        }
        maxHeight={
          iconStyles?.iconFit === "height" || iconStyles?.iconFit === "square"
            ? `${iconSize}px`
            : "auto"
        }
      />
    )
  )
}

const Cta = ({
  section,
  sectionTitle,
  iconAttribute,
  iconStyles,
  button,
  layout, // "stack" | "sideBySide"
}) => {
  const isSideBySide = layout === "sideBySide" // Split layout logic

  // For stack layout, set text justification from SectionTitle
  const textAlign = sectionTitle?.align || "left"
  const stackAlignItems =
    textAlign === "left" ? "start" : textAlign === "right" ? "end" : "center"
  const stackButtonJustify =
    textAlign === "left"
      ? "flex-start"
      : textAlign === "right"
        ? "flex-end"
        : "center"

  // Mirrors SectionTitle's internal width
  const resolveMaxWidthFromTitle = () => {
    const tmw = sectionTitle?.titleMaxWidth
    if (tmw === "fullWidth") return "100%"
    if (tmw === "wide") return 56
    return 42
  }

  // Side-by-side ignores width control (control hidden in Strapi)
  const contentMaxWidthToken = isSideBySide
    ? "100%"
    : resolveMaxWidthFromTitle()

  // Measures actual pixel width of the title wrapper to align the button column
  const titleWrapRef = useRef(null)
  const [measuredPx, setMeasuredPx] = useState(0)

  useEffect(() => {
    const el = titleWrapRef.current
    if (!el) return

    const update = () => setMeasuredPx(el.offsetWidth || 0)
    update()

    let ro
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(update)
      ro.observe(el)
    } else {
      window.addEventListener("resize", update)
    }

    return () => {
      if (ro) ro.disconnect()
      else window.removeEventListener("resize", update)
    }
  }, [contentMaxWidthToken, isSideBySide])

  return (
    <Section {...section}>
      <Reveal cascade>
        <Flex
          flexDirection={isSideBySide ? { _: "column", md: "row" } : "column"}
          justifyContent="space-between"
          alignItems="center"
        >
          {/* Title column */}
          <Flex
            width={isSideBySide ? { _: "100%", md: "60%" } : "100%"}
            flexDirection="column"
            alignItems={isSideBySide ? "start" : stackAlignItems}
          >
            {/* This wrapper defines the “invisible wall” width */}
            <Box
              ref={titleWrapRef}
              width="100%"
              maxWidth={contentMaxWidthToken}
            >
              {renderIcon({ iconAttribute, iconStyles })}
              {sectionTitle && (
                <SectionTitle
                  {...sectionTitle}
                  // Stack layout follow the justification setting. Side-by-side keeps left alignment
                  align={isSideBySide ? "left" : sectionTitle?.align}
                />
              )}
            </Box>
          </Flex>

          {/* Button column */}
          {button && (
            <Flex
              mt={isSideBySide ? { _: 6, md: 0 } : 6}
              alignItems="center"
              justifyContent={
                isSideBySide
                  ? { _: "flex-start", md: "flex-end" }
                  : stackButtonJustify
              }
              width={isSideBySide ? { _: "100%", md: "40%" } : "100%"}
            >
              {/* Matches title wrapper width to align edges */}
              <Box
                width="100%"
                maxWidth={{
                  _: "100%",
                  md: measuredPx > 0 ? `${measuredPx}px` : contentMaxWidthToken,
                }}
                ml={isSideBySide ? { _: 0, md: "auto" } : undefined}
                display="flex"
                justifyContent={
                  isSideBySide
                    ? { _: "flex-start", md: "flex-end" }
                    : stackButtonJustify
                }
              >
                <Button aria-label="CTA in section of a page" {...button} />
              </Box>
            </Flex>
          )}
        </Flex>
      </Reveal>
    </Section>
  )
}

Cta.propTypes = {
  strapi_id: PropTypes.number.isRequired,
  section: PropTypes.shape(Section.strapiProps).isRequired,
  sectionTitle: PropTypes.shape(SectionTitle.strapiProps).isRequired,
  button: PropTypes.shape(Button.strapiProps).isRequired,
  layout: PropTypes.oneOf(["stack", "sideBySide"]),
}

export default Cta

export const query = graphql`
  fragment Cta on STRAPI__COMPONENT_SECTIONS_CTA {
    strapi_id
    CTASection: section {
      ...Section
    }
    sectionTitle {
      ...SectionTitle
    }
    button {
      ...Button
    }
    iconAttribute {
      ...IconAttribute
    }
    iconStyles {
      ...IconStyles
    }
    layout
  }
`
